/**
 * Athlete-facing training-goal options mapped onto the existing Chart 1
 * goal categories. Sport-specific optional fields only — FTP is never
 * required for running or swimming.
 */

export const TRAINING_GOAL_SPORTS = [
  { id: "cycling", label: "Cycling", scopeType: "single_sport" },
  { id: "running", label: "Running", scopeType: "single_sport" },
  { id: "swimming", label: "Swimming", scopeType: "single_sport" },
  {
    id: "triathlon",
    label: "Triathlon",
    scopeType: "multisport",
    sports: ["swimming", "cycling", "running"],
    categorySport: "triathlon",
  },
  {
    id: "duathlon",
    label: "Duathlon",
    scopeType: "multisport",
    sports: ["running", "cycling"],
    categorySport: "duathlon",
  },
];

export const EVENT_DATE_REQUIRED_CATEGORIES = new Set([
  "prepare_for_race",
  "prepare_for_audax",
  "prepare_for_brevet",
  "complete_5k",
  "complete_half_marathon",
  "complete_marathon",
  "complete_ultra",
  "prepare_for_open_water",
  "prepare_for_triathlon_swim",
  "prepare_for_event",
  "complete_multisport_event",
  "prepare_for_triathlon",
  "prepare_for_duathlon",
]);

const EVENT_FALLBACK_CATEGORY = {
  prepare_for_race: "ride_longer_without_fatigue",
  prepare_for_audax: "ride_longer_without_fatigue",
  prepare_for_brevet: "ride_longer_without_fatigue",
  complete_5k: "increase_distance_safely",
  complete_half_marathon: "increase_distance_safely",
  complete_marathon: "increase_distance_safely",
  complete_ultra: "increase_distance_safely",
  prepare_for_open_water: "improve_endurance",
  prepare_for_triathlon_swim: "improve_endurance",
  prepare_for_event: "increase_distance_safely",
  complete_multisport_event: "improve_multisport_endurance",
  prepare_for_triathlon: "improve_multisport_endurance",
  prepare_for_duathlon: "improve_multisport_endurance",
};

const NO_TARGET_REQUIRED = new Set([
  "general_fitness",
  "return_to_cycling",
  "return_to_running",
  "return_to_swimming",
  "return_to_activity",
  "return_to_multisport",
  "improve_technique",
]);

export const TRAINING_GOAL_CATEGORIES = {
  cycling: [
    { value: "ride_longer_without_fatigue", label: "Improve general cycling endurance" },
    { value: "stay_with_faster_group", label: "Prepare for group rides" },
    { value: "improve_ftp", label: "Improve FTP" },
    { value: "prepare_for_race", label: "Sportive / gran fondo / race" },
    { value: "prepare_for_audax", label: "Audax / brevet" },
    { value: "improve_climbing", label: "Climbing" },
    { value: "improve_20_minute_power", label: "Time trial" },
    { value: "return_to_cycling", label: "Return to cycling" },
  ],
  running: [
    { value: "increase_distance_safely", label: "Run more consistently" },
    { value: "improve_5k_time", label: "Improve 5K performance" },
    { value: "improve_10k_time", label: "Improve 10K performance" },
    { value: "complete_half_marathon", label: "Finish a half marathon" },
    { value: "complete_marathon", label: "Finish a marathon" },
    { value: "complete_5k", label: "Finish a 5K event" },
    { value: "return_to_running", label: "Return to running" },
  ],
  swimming: [
    { value: "improve_endurance", label: "Build swim fitness" },
    { value: "improve_technique", label: "Improve technique" },
    { value: "prepare_for_open_water", label: "Prepare for an open-water event" },
    { value: "prepare_for_triathlon_swim", label: "Prepare for a triathlon swim" },
    { value: "return_to_swimming", label: "Return to swimming" },
  ],
  triathlon: [
    { value: "prepare_for_triathlon", label: "Prepare for a triathlon" },
    { value: "improve_multisport_endurance", label: "Build triathlon endurance" },
    { value: "general_fitness", label: "General multisport fitness" },
  ],
  duathlon: [
    { value: "prepare_for_duathlon", label: "Prepare for a duathlon" },
    { value: "improve_multisport_endurance", label: "Build duathlon endurance" },
    { value: "general_fitness", label: "General multisport fitness" },
  ],
};

export const WEEKDAY_OPTIONS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 7, label: "Sun" },
];

export const GOAL_HORIZON_MONTH_OPTIONS = [3, 4, 5, 6, 7, 8, 9, 10];

export const TRAINING_BLOCK_LENGTH_WEEKS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export const QUICK_PLAN_DURATION_OPTIONS = [1, 2, 3, 4, 6, 8, 10];

export const START_PREFERENCE_OPTIONS = [
  { id: "START_TODAY", label: "Today" },
  { id: "START_TOMORROW", label: "Tomorrow" },
  { id: "NEXT_AVAILABLE", label: "Next available day" },
  { id: "START_NEXT_WEEK", label: "Next week" },
  { id: "CUSTOM_DATE", label: "Custom date" },
];

export const ISO_WEEKDAY_NAMES = {
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
  7: "sunday",
};

export const WEEKDAY_NAME_TO_ISO = Object.fromEntries(
  Object.entries(ISO_WEEKDAY_NAMES).map(([iso, name]) => [name, Number(iso)]),
);

export const targetDateFromHorizonMonths = (months, fromDate = new Date()) => {
  const count = Number(months);
  if (!Number.isInteger(count) || count < 1) {
    return "";
  }
  const source = fromDate instanceof Date ? fromDate : new Date(fromDate);
  const year = source.getFullYear();
  const monthIndex = source.getMonth() + count;
  const day = source.getDate();
  const resolved = new Date(year, monthIndex, 1);
  const lastDay = new Date(resolved.getFullYear(), resolved.getMonth() + 1, 0).getDate();
  resolved.setDate(Math.min(day, lastDay));
  const yyyy = resolved.getFullYear();
  const mm = String(resolved.getMonth() + 1).padStart(2, "0");
  const dd = String(resolved.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const completionTarget = () => ({
  metric_type: "completion",
  value: true,
  unit: "bool",
  provenance: { source: "athlete" },
});

export const sportConfig = (sportId) =>
  TRAINING_GOAL_SPORTS.find((item) => item.id === sportId) || TRAINING_GOAL_SPORTS[0];

export const CUSTOM_GOAL_CATEGORY = "custom";

export const CUSTOM_GOAL_OPTION = {
  value: CUSTOM_GOAL_CATEGORY,
  label: "Other / Custom Goal",
};

export const CUSTOM_GOAL_STORAGE_CATEGORY = "general_fitness";

export const categoriesForSport = (sportId) =>
  TRAINING_GOAL_CATEGORIES[sportId] || TRAINING_GOAL_CATEGORIES.cycling;

export const categoriesForSportWithCustom = (sportId) => [
  ...categoriesForSport(sportId),
  CUSTOM_GOAL_OPTION,
];

export const isEventCategory = (category) =>
  EVENT_DATE_REQUIRED_CATEGORIES.has(category);

const formatLocalTime = (totalMinutes) => {
  const clamped = Math.max(0, Math.min(totalMinutes, 24 * 60));
  const hours = Math.floor(clamped / 60);
  const minutes = clamped % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const goalMeasurementsEqual = (left, right) => {
  if (!left && !right) return true;
  if (!left || !right) return false;
  return left.metric_type === right.metric_type
    && left.value === right.value
    && left.unit === right.unit;
};

export const dayMaxMinutesFromAvailability = (availability) => {
  const caps = {};
  for (const day of availability?.weekly_pattern?.days || []) {
    const weekday = Number(day.weekday);
    if (!Number.isInteger(weekday) || weekday < 1 || weekday > 7) continue;
    let maxMinutes = 0;
    for (const window of day.windows || []) {
      if (window?.window_type !== "available") continue;
      const cap = Number(window?.maximum_session_duration_minutes);
      if (Number.isFinite(cap) && cap > 0) {
        maxMinutes = Math.max(maxMinutes, cap);
      }
    }
    if (maxMinutes > 0) {
      caps[weekday] = maxMinutes;
    }
  }
  return caps;
};

export const resolveDaySessionCap = ({
  weekday,
  dayMaxMinutes = {},
  longestSessionMinutes = "",
  defaultCap = 90,
} = {}) => {
  const fromDay = Number(dayMaxMinutes?.[weekday]);
  if (Number.isFinite(fromDay) && fromDay >= 5) {
    return Math.min(fromDay, 720);
  }
  const longest = Number.parseInt(longestSessionMinutes, 10);
  if (Number.isFinite(longest) && longest >= 15) {
    return Math.min(longest, 720);
  }
  return defaultCap;
};

export const goalScopeChanged = (existingGoal, nextPayload = {}) => {
  if (!existingGoal) return false;
  if (existingGoal.scope_type !== nextPayload.scope_type) return true;
  if (nextPayload.scope_type === "multisport") {
    const previous = [...(existingGoal.sports || [])].sort().join(",");
    const next = [...(nextPayload.sports || [])].sort().join(",");
    return previous !== next;
  }
  return (existingGoal.sport || null) !== (nextPayload.sport || null);
};

export const buildGoalPatchPayload = (existingGoal, nextPayload = {}) => {
  if (!existingGoal) {
    return { patch: nextPayload, changed: true, requiresCreate: true };
  }
  if (goalScopeChanged(existingGoal, nextPayload)) {
    return { patch: nextPayload, changed: true, requiresCreate: true };
  }
  const patch = {};
  const compareFields = [
    "goal_category",
    "goal_subtype",
    "original_text",
    "target_date",
    "success_criteria",
    "motivation",
    "event_id",
  ];
  const clearFields = [];
  for (const field of compareFields) {
    const previous = existingGoal[field] ?? null;
    const next = nextPayload[field] ?? null;
    if (previous === next) continue;
    if (
      nextPayload[field] == null
      && Object.prototype.hasOwnProperty.call(nextPayload, field)
    ) {
      clearFields.push(field);
    } else if (nextPayload[field] != null) {
      patch[field] = nextPayload[field];
    }
  }
  if (Object.prototype.hasOwnProperty.call(nextPayload, "target")) {
    if (!goalMeasurementsEqual(existingGoal.target, nextPayload.target)) {
      if (nextPayload.target == null) clearFields.push("target");
      else patch.target = nextPayload.target;
    }
  } else if (existingGoal?.target) {
    clearFields.push("target");
  }
  return {
    patch,
    clearFields,
    changed: Object.keys(patch).length > 0 || clearFields.length > 0,
    requiresCreate: false,
  };
};

const availabilityDaysSignature = (availability) => JSON.stringify(
  (availability?.weekly_pattern?.days || [])
    .map((day) => ({
      weekday: Number(day.weekday),
      windows: (day.windows || []).map((window) => ({
        window_type: window.window_type,
        start_local_time: window.start_local_time,
        end_local_time: window.end_local_time,
        maximum_session_duration_minutes: window.maximum_session_duration_minutes,
        environment: window.environment,
        preferred_sports: window.preferred_sports,
      })),
    }))
    .sort((left, right) => left.weekday - right.weekday),
);

export const buildAvailabilityPatch = ({
  existingAvailability = null,
  sportId = "cycling",
  weekdays = [],
  timezone = "UTC",
  longestSessionMinutes = "",
  dayMaxMinutes = {},
  preferredLongSessionDay = "",
} = {}) => {
  const nextPayload = buildAvailabilityPayload({
    sportId,
    weekdays,
    timezone,
    longestSessionMinutes,
    dayMaxMinutes,
    existingAvailability,
    preferredLongSessionDay,
  });
  if (!nextPayload) {
    return { payload: null, changed: false };
  }
  if (!existingAvailability) {
    return { payload: nextPayload, changed: true };
  }
  if (
    availabilityDaysSignature(existingAvailability) === availabilityDaysSignature(nextPayload)
    && JSON.stringify(existingAvailability.general_constraints || {})
      === JSON.stringify(nextPayload.general_constraints || {})
  ) {
    return { payload: null, changed: false };
  }
  return {
    payload: {
      expected_revision: existingAvailability.revision
        || existingAvailability.expected_revision,
      schedule_mode: nextPayload.schedule_mode,
      schedule_timezone: nextPayload.schedule_timezone,
      status: nextPayload.status,
      weekly_pattern: nextPayload.weekly_pattern,
      general_constraints: {
        ...(existingAvailability.general_constraints || {}),
        ...(nextPayload.general_constraints || {}),
      },
    },
    changed: true,
  };
};

export const buildAthleteGoalPayload = ({
  sportId = "cycling",
  goalCategory,
  originalText,
  targetDate = "",
  noEventDate = false,
  distanceKm = "",
  ftpWatts = "",
  successCriteria = "",
  goalSubtype = undefined,
  existingGoal = null,
} = {}) => {
  const sport = sportConfig(sportId);
  const trimmedText = String(originalText || "").trim();
  if (!trimmedText) {
    throw new Error("Describe your goal in a sentence.");
  }
  if (!goalCategory) {
    throw new Error("Choose a training goal.");
  }

  let category = goalCategory;
  let resolvedDate = targetDate ? String(targetDate).slice(0, 10) : null;
  if (isEventCategory(category) && (noEventDate || !resolvedDate)) {
    category = EVENT_FALLBACK_CATEGORY[category] || "general_fitness";
    resolvedDate = null;
  }
  if (isEventCategory(category) && !resolvedDate) {
    throw new Error("Add an event date, or say you do not have one yet.");
  }

  const payload = {
    scope_type: sport.scopeType,
    goal_category: category,
    original_text: trimmedText,
    status: "active",
    source: "athlete",
  };

  if (sport.scopeType === "multisport") {
    payload.sports = sport.sports;
  } else {
    payload.sport = sport.id;
  }

  if (resolvedDate) {
    payload.target_date = resolvedDate;
  }
  if (successCriteria && String(successCriteria).trim()) {
    payload.success_criteria = String(successCriteria).trim();
  }
  if (typeof goalSubtype === "string" && goalSubtype.trim()) {
    payload.goal_subtype = goalSubtype.trim();
  } else if (goalSubtype === null) {
    payload.goal_subtype = null;
  }

  const ftp = Number.parseFloat(ftpWatts);
  const distance = Number.parseFloat(distanceKm);
  if (Number.isFinite(ftp) && ftp > 0 && sport.id === "cycling") {
    payload.target = {
      metric_type: "ftp",
      value: ftp,
      unit: "W",
      provenance: { source: "athlete" },
    };
  } else if (Number.isFinite(distance) && distance > 0) {
    const metricType = sport.id === "running"
      ? "running_distance"
      : sport.id === "swimming"
        ? "swimming_distance"
        : "target_distance";
    const unit = sport.id === "swimming" ? "m" : "km";
    payload.target = {
      metric_type: metricType,
      value: sport.id === "swimming" ? distance : distance,
      unit,
      provenance: { source: "athlete" },
    };
  } else if (existingGoal?.target && existingGoal.goal_category === category) {
    payload.target = existingGoal.target;
  } else if (!NO_TARGET_REQUIRED.has(category)) {
    payload.target = completionTarget();
  }

  return payload;
};

export const buildProposedTrainingContext = ({
  sportId,
  goalPayload,
  weekdays = [],
  dayMaxMinutes = {},
  preferredLongSessionDay = "",
  longestSessionMinutes = "",
  startPreference = "START_TOMORROW",
  customStartDate = "",
  activePlanConflictChoice = "",
  hasLivePlan = false,
  horizonWeeks = 4,
} = {}) => {
  const longest = Number(longestSessionMinutes);
  return {
    sport: sportId,
    goal_category: goalPayload.goal_category,
    goal_subtype: goalPayload.goal_subtype ?? null,
    original_text: goalPayload.original_text,
    target: goalPayload.target ?? null,
    target_date: goalPayload.target_date ?? null,
    weekly_training_days: [...weekdays]
      .map((weekday) => Number(weekday))
      .sort((left, right) => left - right)
      .map((weekday) => ({
        weekday,
        max_minutes: Math.max(1, Math.min(720, Number(dayMaxMinutes?.[weekday]) || 60)),
      })),
    preferred_long_session_day: preferredLongSessionDay || null,
    longest_session_minutes: Number.isFinite(longest)
      ? Math.max(15, Math.min(720, longest))
      : null,
    start_preference: startPreference,
    custom_start_date: startPreference === "CUSTOM_DATE" ? (customStartDate || null) : null,
    active_plan_conflict_choice: hasLivePlan ? (activePlanConflictChoice || null) : null,
    horizon_weeks: Number(horizonWeeks),
  };
};

export const buildAvailabilityPayload = ({
  sportId = "cycling",
  weekdays = [],
  timezone = "UTC",
  longestSessionMinutes = "",
  dayMaxMinutes = {},
  existingAvailability = null,
  preferredLongSessionDay = "",
} = {}) => {
  const days = [...new Set((weekdays || []).map((day) => Number(day)))]
    .filter((day) => day >= 1 && day <= 7)
    .sort((a, b) => a - b);
  if (days.length === 0) {
    return null;
  }
  const sport = sportConfig(sportId);
  const preferredSports = sport.scopeType === "multisport"
    ? sport.sports
    : [sport.id];
  const dayCaps = days.map((weekday) => resolveDaySessionCap({
    weekday,
    dayMaxMinutes,
    longestSessionMinutes,
  }));
  const totalMinutes = dayCaps.reduce((sum, minutes) => sum + minutes, 0);
  const longest = Math.max(...dayCaps);
  const payload = {
    schedule_mode: existingAvailability?.schedule_mode || "fixed_weekly",
    schedule_timezone: timezone || existingAvailability?.schedule_timezone || "UTC",
    status: "active",
    weekly_pattern: {
      days: days.map((weekday, index) => {
        const minutes = dayCaps[index];
        const endMinutes = Math.min(20 * 60, 6 * 60 + minutes);
        return {
          weekday,
          windows: [
            {
              window_id: `day-${weekday}`,
              window_type: "available",
              start_local_time: "06:00",
              end_local_time: formatLocalTime(endMinutes),
              end_day_offset: 0,
              maximum_session_duration_minutes: minutes,
              environment: "either",
              preferred_sports: preferredSports,
            },
          ],
        };
      }),
    },
    general_constraints: {
      ...(existingAvailability?.general_constraints || {}),
      maximum_training_days_per_week: days.length,
      maximum_total_available_minutes_per_week: totalMinutes,
      longest_session_minutes: Math.max(15, Math.min(longest, 720)),
      ...(preferredLongSessionDay
        ? { preferred_long_session_day: preferredLongSessionDay }
        : {}),
    },
  };
  return payload;
};
